/**
 * Admin Users screen: displaying and editing a user's per-account project limit.
 *
 * Kept pure and separate from the React table for the usual reason — `web` has
 * no test runner of its own and must not gain one, so the testable part is the
 * part with the decisions in it.
 *
 * The validation here deliberately MIRRORS setProjectLimitV2's server-side
 * check (functions/src/projects/setProjectLimit.ts). It is a courtesy, not a
 * control: the server re-validates everything, because anything shipped to a
 * browser can be edited by whoever has it.
 */

/** Limit applied when users/{uid}.projectLimit is absent. Mirrors
 *  DEFAULT_PROJECT_LIMIT in functions/src/projects/projectLimit.ts. */
export const DEFAULT_PROJECT_LIMIT = 1;

/** Mirrors MAX_SETTABLE_PROJECT_LIMIT server-side. */
export const MAX_PROJECT_LIMIT = 10000;

/** A stored value is usable only if it is a whole number ≥ 0. */
function storedValue(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= MAX_PROJECT_LIMIT
    ? value
    : null;
}

/**
 * How the limit reads in the table.
 *
 * "1 (default)" and "1" are different facts: the first means nobody has ever
 * set a limit for this account, the second means an admin chose 1. Collapsing
 * them would hide whether an account has been considered at all.
 *
 * An explicit 0 must survive — it is a real setting (no new projects), and a
 * falsy check would swallow it into the default.
 */
export function formatProjectLimit(value: unknown): string {
  const n = storedValue(value);
  if (n === null) return `${DEFAULT_PROJECT_LIMIT} (default)`;
  return String(n);
}

export type ParseResult =
  | { ok: true; value: number }
  | { ok: false; reason: string };

/**
 * Parse what an admin typed. Rejects the same values the server rejects, so a
 * bad entry is refused in the field instead of as a round-trip error.
 */
export function parseProjectLimitInput(raw: string): ParseResult {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { ok: false, reason: "Enter a number." };

  // Strict digits only. Number() would accept "1e3", "0x10" and " 1 ",
  // none of which an admin meant to type into a project-limit box.
  if (!/^\d+$/.test(trimmed)) {
    return { ok: false, reason: "Enter a whole number, digits only." };
  }

  const value = Number(trimmed);
  if (!Number.isInteger(value) || value < 0) {
    return { ok: false, reason: "Enter a whole number of 0 or more." };
  }
  if (value > MAX_PROJECT_LIMIT) {
    return { ok: false, reason: `The maximum is ${MAX_PROJECT_LIMIT}.` };
  }
  return { ok: true, value };
}

/**
 * Whether submitting `next` would change anything, treating an absent stored
 * value as the default. Lets the caller skip a pointless write — and a
 * pointless projectLimitSetBy/At stamp claiming a change that did not happen.
 */
export function isProjectLimitUnchanged(stored: unknown, next: number): boolean {
  const current = storedValue(stored) ?? DEFAULT_PROJECT_LIMIT;
  return current === next;
}
